import { Component, inject, OnInit } from '@angular/core'
import { Table } from '@ui/Table'
import PaymentMethod from '@account/PaymentMethod'
import { PaymentMethodService } from '@account/Services/PaymentMethodService'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ConfirmDeleteDialogComponent } from '@account/Dialogs/ConfirmDeleteDialog.Component'
import HttpError from '@ui/HttpError'
import { PageComponent } from '@account/Components/ui/Page/Page.Component'
import { CdkTableComponent } from '@ui/Components/Table/CdkTable.Component'

@Component({
	selector: 'payment-methods',
	templateUrl: './PaymentMethods.Component.html',
	imports: [PageComponent, CdkTableComponent],
})
export class PaymentMethodsComponent
	extends Table<PaymentMethod>
	implements OnInit
{
	public displayColumns: string[] = ['card', 'expiry', 'default', 'delete']

	private confirmDeleteDialogComponentMatDialogRef: MatDialogRef<ConfirmDeleteDialogComponent>

	private readonly paymentMethodService: PaymentMethodService

	private readonly dialog: MatDialog

	constructor() {
		super()
		this.paymentMethodService = inject(PaymentMethodService)
		this.dialog = inject(MatDialog)
	}

	public ngOnInit() {
		this.tableEntities = [
			{
				columnDef: 'card',
				header: 'Type',
				accessorCallback(entity: PaymentMethod): string {
					return `${entity.brand} ending in ${entity.last4}`
				},
			},
			{
				columnDef: 'expiry',
				header: 'Expiry',
				accessorKey: 'expiry',
			},
			{
				columnDef: 'default',
				header: 'Default',
				type: 'action',
				action: {
					color: 'primary',
					title: 'Set Default',
					disabled: (entity: PaymentMethod): boolean => entity.defaultMethod,
					output: async (entity: PaymentMethod) => {
						if (entity.defaultMethod) {
							return
						}

						this.confirmDeleteDialogComponentMatDialogRef = this.dialog.open(
							ConfirmDeleteDialogComponent,
						)

						this.confirmDeleteDialogComponentMatDialogRef
							.afterClosed()
							.subscribe(async (confirm: boolean) => {
								if (confirm) {
									try {
										entity.defaultMethod = !entity.defaultMethod

										await this.paymentMethodService.update(entity)

										await this.fetchCards()
									} catch (error) {
										if (!(error instanceof HttpError)) {
											throw error
										}
									}
								}
							})
					},
				},
			},
			{
				columnDef: 'delete',
				header: 'Delete',
				type: 'action',
				action: {
					color: 'warn',
					title: 'Delete',
					output: async (entity: PaymentMethod) => {
						this.confirmDeleteDialogComponentMatDialogRef = this.dialog.open(
							ConfirmDeleteDialogComponent,
						)

						this.confirmDeleteDialogComponentMatDialogRef
							.afterClosed()
							.subscribe(async (confirm: boolean) => {
								if (confirm) {
									try {
										await this.paymentMethodService.deleteMethod(entity)

										await this.fetchCards()
									} catch (error) {
										if (!(error instanceof HttpError)) {
											throw error
										}
									}
								}
							})
					},
				},
			},
		]

		this.fetchCards()
	}

	private readonly fetchCards = async () => {
		try {
			const methods = await this.paymentMethodService.filter()

			this.setTableData(methods)
		} catch (error) {
			if (!(error instanceof HttpError)) {
				throw error
			}
		}
	}
}
